/**
 * /attendance/config — work-pattern + device-mapping config and clock-event
 * (dangling-punch) review (ADR-0014 Phase 4 PR-4). The final Attendance screen.
 * HR-gated, mirroring leaveAdmin's tabbed page + restricted guard. Off PR-0's
 * AttendanceWorkPattern / AttendanceDeviceMapping CRUD + GET clock-events.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  createDeviceMapping,
  createLocation,
  deleteDeviceMapping,
  deleteLocation,
  deleteWorkPattern,
  listClockEvents,
  listDeviceMappings,
  listLocations,
  listWorkPatterns,
  upsertWorkPattern,
} from '../api/attendance.js';
import { listEmployees } from '../api/employees.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate, formatNumber } from '../lib/format.js';
import { GeofenceMapPicker } from '../components/GeofenceMapPicker.js';

export const attendanceConfigRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/attendance/config',
  component: AttendanceConfigPage,
});

type Tab = 'patterns' | 'devices' | 'locations' | 'events';

function AttendanceConfigPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';
  const [tab, setTab] = useState<Tab>('patterns');

  if (!isHr) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">Restricted to HR / admin / owner roles.</Alert>
      </section>
    );
  }

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Attendance config</h1>
        <p className="text-sm text-gray-400">Work patterns and device mappings drive overtime pricing and the punch-log import. Review raw punches for dangling clocks.</p>
      </header>
      <nav className="flex gap-2 border-b border-gray-900">
        <TabButton active={tab === 'patterns'} onClick={() => setTab('patterns')}>Work patterns</TabButton>
        <TabButton active={tab === 'devices'} onClick={() => setTab('devices')}>Device mappings</TabButton>
        <TabButton active={tab === 'locations'} onClick={() => setTab('locations')}>Locations</TabButton>
        <TabButton active={tab === 'events'} onClick={() => setTab('events')}>Clock events</TabButton>
      </nav>
      {tab === 'patterns' ? <WorkPatternsPanel /> : tab === 'devices' ? <DeviceMappingsPanel /> : tab === 'locations' ? <LocationsPanel /> : <ClockEventsPanel />}
    </section>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={`px-3 py-2 text-sm border-b-2 ${active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'}`}>
      {children}
    </button>
  );
}

function useEmployees() {
  const q = useQuery({ queryKey: ['employees', 'picker'], queryFn: () => listEmployees({ status: 'active', limit: 200 }) });
  const list = q.data?.employees ?? [];
  const nameById = new Map(list.map((e) => [e.id, `${e.firstName} ${e.lastName}`]));
  return { list, nameById };
}

const inputCls = 'mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';

// --- Work patterns ---------------------------------------------------------

const PatternSchema = z.object({
  employeeId: z.string().min(1, 'Select an employee'),
  operatingMode: z.enum(['salaried_exception', 'hourly']),
  standardHoursPerWeek: z.coerce.number().positive().max(168),
  overtimeMultiplier: z.coerce.number().positive().max(10),
  locationId: z.string().optional(),
});
type PatternValues = z.infer<typeof PatternSchema>;

function WorkPatternsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const { list: employees, nameById } = useEmployees();
  const patternsQuery = useQuery({ queryKey: ['attendance', 'work-patterns'], queryFn: listWorkPatterns });
  const locationsQuery = useQuery({ queryKey: ['attendance', 'locations'], queryFn: listLocations });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<PatternValues>({
    resolver: zodResolver(PatternSchema),
    defaultValues: { employeeId: '', operatingMode: 'salaried_exception', standardHoursPerWeek: 40, overtimeMultiplier: 1.5, locationId: '' },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance', 'work-patterns'] });
  const upsertMut = useMutation({
    mutationFn: (v: PatternValues) => upsertWorkPattern(v.employeeId, { operatingMode: v.operatingMode, standardHoursPerWeek: v.standardHoursPerWeek, overtimeMultiplier: v.overtimeMultiplier, locationId: v.locationId ? v.locationId : null }),
    onSuccess: async () => { setError(null); setSuccess('Work pattern saved.'); reset({ employeeId: '', operatingMode: 'salaried_exception', standardHoursPerWeek: 40, overtimeMultiplier: 1.5, locationId: '' }); await invalidate(); },
    onError: (e) => { setSuccess(null); setError(e instanceof ApiError ? e.message : 'Save failed.'); },
  });
  const deleteMut = useMutation({ mutationFn: (employeeId: string) => deleteWorkPattern(employeeId), onSuccess: () => void invalidate() });

  const patterns = patternsQuery.data?.patterns ?? [];

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit((v) => upsertMut.mutate(v))} className="rounded border border-gray-900 p-4 space-y-3 max-w-xl" noValidate>
        <h2 className="text-sm uppercase tracking-wide text-gray-400">Set a work pattern</h2>
        {success ? <div className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{success}</div> : null}
        <label className="block text-xs uppercase text-gray-400">
          Employee
          <select {...register('employeeId')} className={inputCls}>
            <option value="">Select an employee…</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
          </select>
          {errors.employeeId ? <span className="text-xs text-red-400">{errors.employeeId.message}</span> : null}
        </label>
        <label className="block text-xs uppercase text-gray-400">
          Operating mode
          <select {...register('operatingMode')} className={inputCls}>
            <option value="salaried_exception">Salaried (exception-tracked)</option>
            <option value="hourly">Hourly</option>
          </select>
        </label>
        <div className="flex gap-3">
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Standard hours / week
            <input type="number" step="0.5" {...register('standardHoursPerWeek')} className={inputCls} />
            {errors.standardHoursPerWeek ? <span className="text-xs text-red-400">{errors.standardHoursPerWeek.message}</span> : null}
          </label>
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Overtime multiplier
            <input type="number" step="0.05" {...register('overtimeMultiplier')} className={inputCls} />
            {errors.overtimeMultiplier ? <span className="text-xs text-red-400">{errors.overtimeMultiplier.message}</span> : null}
          </label>
        </div>
        <label className="block text-xs uppercase text-gray-400">
          Geofence site
          <select {...register('locationId')} className={inputCls}>
            <option value="">None (not geofenced)</option>
            {(locationsQuery.data?.locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name} ({l.radiusMeters} m)</option>)}
          </select>
        </label>
        {error ? <Alert variant="error">{error}</Alert> : null}
        <button type="submit" disabled={isSubmitting || upsertMut.isPending} className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50">
          {upsertMut.isPending ? 'Saving…' : 'Save pattern'}
        </button>
      </form>

      {patternsQuery.isLoading ? <Placeholder>Loading…</Placeholder> : patterns.length === 0 ? <Placeholder>No work patterns set. Employees without one use the default (salaried, 40h, 1.5×).</Placeholder> : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="text-left px-4 py-2">Employee</th><th className="text-left px-4 py-2">Mode</th><th className="text-right px-4 py-2">Std hrs/wk</th><th className="text-right px-4 py-2">OT ×</th><th className="text-right px-4 py-2">Action</th></tr>
            </thead>
            <tbody>
              {patterns.map((p) => (
                <tr key={p.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 text-gray-300">{nameById.get(p.employeeId) ?? <span className="font-mono text-xs text-gray-500">{p.employeeId}</span>}</td>
                  <td className="px-4 py-3 text-gray-400">{p.operatingMode}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.standardHoursPerWeek)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.overtimeMultiplier)}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => deleteMut.mutate(p.employeeId)} disabled={deleteMut.isPending} className="text-xs rounded border border-gray-700 px-2 py-1 hover:bg-gray-900 disabled:opacity-50">Reset</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Device mappings -------------------------------------------------------

const MappingSchema = z.object({
  deviceId: z.string().min(1, 'Required'),
  externalUserId: z.string().min(1, 'Required'),
  employeeId: z.string().min(1, 'Select an employee'),
});
type MappingValues = z.infer<typeof MappingSchema>;

function DeviceMappingsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const { list: employees, nameById } = useEmployees();
  const mappingsQuery = useQuery({ queryKey: ['attendance', 'device-mappings'], queryFn: listDeviceMappings });
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<MappingValues>({
    resolver: zodResolver(MappingSchema),
    defaultValues: { deviceId: '', externalUserId: '', employeeId: '' },
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance', 'device-mappings'] });
  const createMut = useMutation({
    mutationFn: (v: MappingValues) => createDeviceMapping(v),
    onSuccess: async () => { setError(null); reset({ deviceId: '', externalUserId: '', employeeId: '' }); await invalidate(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Create failed.'),
  });
  const deleteMut = useMutation({ mutationFn: (id: string) => deleteDeviceMapping(id), onSuccess: () => void invalidate() });

  const mappings = mappingsQuery.data?.mappings ?? [];

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit((v) => createMut.mutate(v))} className="rounded border border-gray-900 p-4 space-y-3 max-w-xl" noValidate>
        <h2 className="text-sm uppercase tracking-wide text-gray-400">Map a device user to an employee</h2>
        <div className="flex gap-3">
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Device ID
            <input {...register('deviceId')} className={inputCls} placeholder="ZK-1" />
            {errors.deviceId ? <span className="text-xs text-red-400">{errors.deviceId.message}</span> : null}
          </label>
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Device user ID
            <input {...register('externalUserId')} className={inputCls} placeholder="47" />
            {errors.externalUserId ? <span className="text-xs text-red-400">{errors.externalUserId.message}</span> : null}
          </label>
        </div>
        <label className="block text-xs uppercase text-gray-400">
          Employee
          <select {...register('employeeId')} className={inputCls}>
            <option value="">Select an employee…</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
          </select>
          {errors.employeeId ? <span className="text-xs text-red-400">{errors.employeeId.message}</span> : null}
        </label>
        {error ? <Alert variant="error">{error}</Alert> : null}
        <button type="submit" disabled={createMut.isPending} className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50">
          {createMut.isPending ? 'Adding…' : 'Add mapping'}
        </button>
      </form>

      {mappingsQuery.isLoading ? <Placeholder>Loading…</Placeholder> : mappings.length === 0 ? <Placeholder>No device mappings yet. The punch-log import resolves punches via these.</Placeholder> : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="text-left px-4 py-2">Device</th><th className="text-left px-4 py-2">Device user</th><th className="text-left px-4 py-2">Employee</th><th className="text-right px-4 py-2">Action</th></tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{m.deviceId}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{m.externalUserId}</td>
                  <td className="px-4 py-3 text-gray-300">{nameById.get(m.employeeId) ?? <span className="font-mono text-xs text-gray-500">{m.employeeId}</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => deleteMut.mutate(m.id)} disabled={deleteMut.isPending} className="text-xs rounded border border-gray-700 px-2 py-1 hover:bg-gray-900 disabled:opacity-50">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Locations (geofence sites, Phase 6) -----------------------------------

const LocationSchema = z.object({
  name: z.string().min(1, 'Required'),
  latitude: z.coerce.number().min(-90).max(90),
  longitude: z.coerce.number().min(-180).max(180),
  radiusMeters: z.coerce.number().int().min(50).max(100_000),
});
type LocationValues = z.infer<typeof LocationSchema>;

function LocationsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['attendance', 'locations'], queryFn: listLocations });
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance', 'locations'] });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<LocationValues>({
    resolver: zodResolver(LocationSchema),
    defaultValues: { name: '', latitude: 0, longitude: 0, radiusMeters: 100 },
  });
  const createMut = useMutation({
    mutationFn: (v: LocationValues) => createLocation(v),
    onSuccess: async () => { setError(null); reset({ name: '', latitude: 0, longitude: 0, radiusMeters: 100 }); await invalidate(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Create failed.'),
  });
  const deleteMut = useMutation({ mutationFn: (id: string) => deleteLocation(id), onSuccess: () => void invalidate() });

  const useMyLocation = (): void => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setValue('latitude', Number(pos.coords.latitude.toFixed(7)));
        setValue('longitude', Number(pos.coords.longitude.toFixed(7)));
      });
    }
  };

  const locations = query.data?.locations ?? [];

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit((v) => createMut.mutate(v))} className="rounded border border-gray-900 p-4 space-y-3 max-w-xl" noValidate>
        <h2 className="text-sm uppercase tracking-wide text-gray-400">Add a geofence site</h2>
        <label className="block text-xs uppercase text-gray-400">
          Name
          <input {...register('name')} className={inputCls} placeholder="Head office" />
          {errors.name ? <span className="text-xs text-red-400">{errors.name.message}</span> : null}
        </label>
        <div className="flex gap-3">
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Latitude
            <input type="number" step="0.0000001" {...register('latitude')} className={inputCls} />
            {errors.latitude ? <span className="text-xs text-red-400">{errors.latitude.message}</span> : null}
          </label>
          <label className="block text-xs uppercase text-gray-400 flex-1">
            Longitude
            <input type="number" step="0.0000001" {...register('longitude')} className={inputCls} />
            {errors.longitude ? <span className="text-xs text-red-400">{errors.longitude.message}</span> : null}
          </label>
          <label className="block text-xs uppercase text-gray-400 w-32">
            Radius (m)
            <input type="number" step="1" {...register('radiusMeters')} className={inputCls} />
            {errors.radiusMeters ? <span className="text-xs text-red-400">{errors.radiusMeters.message}</span> : null}
          </label>
        </div>
        <GeofenceMapPicker
          latitude={Number(watch('latitude'))}
          longitude={Number(watch('longitude'))}
          radiusMeters={Number(watch('radiusMeters'))}
          onChange={(lat, lng) => { setValue('latitude', lat); setValue('longitude', lng); }}
        />
        <div className="flex gap-2">
          <button type="submit" disabled={createMut.isPending} className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50">{createMut.isPending ? 'Adding…' : 'Add site'}</button>
          <button type="button" onClick={useMyLocation} className="rounded border border-gray-700 text-sm px-4 py-2 hover:bg-gray-900">Use my current location</button>
        </div>
        {error ? <Alert variant="error">{error}</Alert> : null}
      </form>

      {query.isLoading ? <Placeholder>Loading…</Placeholder> : locations.length === 0 ? <Placeholder>No geofence sites yet. Employees with no site are not geofenced.</Placeholder> : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="text-left px-4 py-2">Site</th><th className="text-right px-4 py-2">Latitude</th><th className="text-right px-4 py-2">Longitude</th><th className="text-right px-4 py-2">Radius (m)</th><th className="text-right px-4 py-2">Action</th></tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 text-gray-300">{l.name}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-300">{Number(l.latitude)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-300">{Number(l.longitude)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{l.radiusMeters}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => deleteMut.mutate(l.id)} disabled={deleteMut.isPending} className="text-xs rounded border border-gray-700 px-2 py-1 hover:bg-gray-900 disabled:opacity-50">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Clock events (review) -------------------------------------------------

function ClockEventsPanel(): JSX.Element {
  const { list: employees, nameById } = useEmployees();
  const [employeeId, setEmployeeId] = useState('');
  const [outOfRangeOnly, setOutOfRangeOnly] = useState(false);
  const query = useQuery({
    queryKey: ['attendance', 'clock-events', employeeId, outOfRangeOnly],
    queryFn: () => listClockEvents({ ...(employeeId ? { employeeId } : {}), ...(outOfRangeOnly ? { outOfRangeOnly: true } : {}) }),
  });
  const events = query.data?.events ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4">
        <label className="block text-xs uppercase text-gray-400 max-w-xs flex-1">
          Filter by employee
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputCls}>
            <option value="">All employees</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-400 pb-2">
          <input type="checkbox" checked={outOfRangeOnly} onChange={(e) => setOutOfRangeOnly(e.target.checked)} />
          Out-of-area only
        </label>
      </div>

      {query.isLoading ? <Placeholder>Loading…</Placeholder> : query.isError ? <Placeholder error>Could not load punches.</Placeholder> : events.length === 0 ? <Placeholder>No punches recorded.</Placeholder> : (
        <div className="rounded border border-gray-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Employee</th><th className="text-left px-4 py-2">Direction</th><th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">Geofence</th></tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-gray-900">
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{formatDate(e.occurredAt)} {e.occurredAt.slice(11, 16)}</td>
                  <td className="px-4 py-3 text-gray-300">{nameById.get(e.employeeId) ?? <span className="font-mono text-xs text-gray-500">{e.employeeId}</span>}</td>
                  <td className="px-4 py-3 text-gray-300">{e.direction === 'clock_in' ? 'Clock in' : 'Clock out'}</td>
                  <td className="px-4 py-3 text-gray-500">{e.source}</td>
                  <td className="px-4 py-3">
                    {e.geofenceOutOfRange ? <span className="text-xs px-2 py-0.5 rounded border border-yellow-900 bg-yellow-950 text-yellow-400">out of area</span> : <span className="text-xs text-gray-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>{children}</div>;
}
